import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
    LayoutDashboard, Users, Car, Map, Calendar, Newspaper, PlayCircle,
    Star, Settings, BarChart3, LogOut, Menu, X, ChevronDown, Wallet, FileText, FolderOpen, Layers, Layout, MessageCircle
} from 'lucide-react'
import { useAuthStore, useLanguageStore } from '../../lib/store'

export default function AdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [expandedMenu, setExpandedMenu] = useState<string | null>('content')
    const location = useLocation()
    const { user, logout } = useAuthStore()
    const { language } = useLanguageStore()

    const menuSections = [
        {
            id: 'main',
            items: [
                { path: '/admin', icon: LayoutDashboard, label: { en: 'Dashboard', vi: 'Tổng quan' }, exact: true },
            ]
        },
        {
            id: 'users',
            title: { en: 'Users & Orders', vi: 'Người dùng & Đơn hàng' },
            items: [
                { path: '/admin/users', icon: Users, label: { en: 'Users', vi: 'Người dùng' } },
                { path: '/admin/bookings', icon: Calendar, label: { en: 'Bookings', vi: 'Đơn đặt' } },
                { path: '/admin/withdrawals', icon: Wallet, label: { en: 'Withdrawals', vi: 'Rút tiền' } },
            ]
        },
        {
            id: 'content',
            title: { en: 'Content', vi: 'Nội dung' },
            items: [
                { path: '/admin/page-builder', icon: Layers, label: { en: 'Page Builder', vi: 'Trình tạo trang' } },
                { path: '/admin/article-layout', icon: Layout, label: { en: 'Article Layout', vi: 'Bố cục bài viết' } },
                { path: '/admin/vehicles', icon: Car, label: { en: 'Vehicles', vi: 'Xe' } },
                { path: '/admin/tours', icon: Map, label: { en: 'Tours', vi: 'Tour' } },
                { path: '/admin/blog', icon: Newspaper, label: { en: 'Blog Posts', vi: 'Bài viết' } },
                { path: '/admin/categories', icon: FolderOpen, label: { en: 'Categories', vi: 'Danh mục' } },
                { path: '/admin/pages', icon: FileText, label: { en: 'Pages', vi: 'Trang' } },
                { path: '/admin/videos', icon: PlayCircle, label: { en: 'Short Videos', vi: 'Video ngắn' } },
                { path: '/admin/comments', icon: MessageCircle, label: { en: 'Comments', vi: 'Bình luận' } },
                { path: '/admin/reviews', icon: Star, label: { en: 'Reviews', vi: 'Đánh giá' } },
            ]
        },
        {
            id: 'system',
            title: { en: 'System', vi: 'Hệ thống' },
            items: [
                { path: '/admin/analytics', icon: BarChart3, label: { en: 'Analytics', vi: 'Thống kê' } },
                { path: '/admin/settings', icon: Settings, label: { en: 'Settings', vi: 'Cài đặt' } },
            ]
        },
    ]

    const isActive = (path: string, exact?: boolean) => {
        if (exact) return location.pathname === path
        return location.pathname.startsWith(path)
    }

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900 text-gray-300 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-16'
                    }`}
            >
                {/* Header */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
                    {sidebarOpen ? (
                        <Link to="/admin" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold">
                                A
                            </div>
                            <span className="font-bold text-white">Admin Panel</span>
                        </Link>
                    ) : (
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold mx-auto">
                            A
                        </div>
                    )}
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-1.5 hover:bg-gray-800 rounded-lg lg:hidden"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4">
                    {menuSections.map((section) => (
                        <div key={section.id} className="mb-4">
                            {section.title && sidebarOpen && (
                                <button
                                    onClick={() => setExpandedMenu(expandedMenu === section.id ? null : section.id)}
                                    className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase"
                                >
                                    {section.title[language]}
                                    <ChevronDown className={`w-4 h-4 transition ${expandedMenu === section.id ? 'rotate-180' : ''}`} />
                                </button>
                            )}
                            <div className={sidebarOpen && section.title ? (expandedMenu === section.id ? 'block' : 'hidden') : 'block'}>
                                {section.items.map((item) => {
                                    const Icon = item.icon
                                    const active = isActive(item.path, (item as any).exact)

                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition ${active
                                                ? 'bg-primary-600 text-white'
                                                : 'hover:bg-gray-800 hover:text-white'
                                                }`}
                                        >
                                            <Icon className="w-5 h-5 flex-shrink-0" />
                                            {sidebarOpen && <span className="text-sm">{item.label[language]}</span>}
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* User Section */}
                <div className="border-t border-gray-800 p-4">
                    {sidebarOpen ? (
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{user?.fullName}</p>
                                <p className="text-xs text-gray-500">Administrator</p>
                            </div>
                            <button
                                onClick={logout}
                                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"
                            >
                                <LogOut className="w-5 h-5" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={logout}
                            className="w-full flex justify-center p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white"
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Top Bar */}
                <header className="h-16 bg-white border-b flex items-center justify-between px-4 lg:px-6">
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-2 ml-auto">
                        <Link to="/" className="text-sm text-gray-600 hover:text-primary-600">
                            {language === 'en' ? 'View Site' : 'Xem trang'}
                        </Link>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    )
}
